import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'

import { GradeType } from '~/onboarding/utils/grade-color.utils'
import { RootState } from '~/redux/store'
import { TopicId } from '~/dashboard/utils/learningPath.utils'

import {
  CurriculumCertificate,
  CurriculumGradeDataType,
  CurriculumTopicDataType,
  GradeOverviewFromSanity,
  LastActivityCurriculum,
} from '../types/curriculum.types'
import { CurriculumMappingQuizQuestionResponseDto, LastLevelStatusInfo } from '../types/mapping-quiz.types'
import { curriculumSubjectContentDefault } from '../constants/curriculum.const'

export const emptyLastLevelInfo: LastLevelStatusInfo = {
  isLastLevel: false,
  lastLevelIds: [],
  gradeInfo: {
    countryCode: '',
    gradeId: '',
    gradeLevel: 0,
    subjectOrder: 0,
    subject: '',
    topicId: '',
  },
}

export interface CurriculumSliceType {
  gradeListByCountry: GradeType[]
  gradeContent: CurriculumGradeDataType
  curriculumSubjectContent: CurriculumTopicDataType
  lastActivity: LastActivityCurriculum
  curriculumChangeLoading: boolean
  menuCurriculumDropdownExpanded: boolean
  gradePageCurriculumDropdownExpanded: boolean
  footerCurriculumDropdownExpanded: boolean
  curriculumChangeWarning: boolean
  changeGradeSelection?: number
  previousPage?: string
  shouldResetCurriculumMappingQuiz?: boolean
  curriculumMappingQuizQuestion?: CurriculumMappingQuizQuestionResponseDto
  /**
   * The topic whose mapping quiz was started from the entry card — the first take, since that card
   * only shows while the quiz has never been taken there. Keyed `${gradeLevel}-${subjectOrder}`.
   * The topic page reads it as the "start here" hint; a retake or a started card clears it.
   */
  firstMappingQuizTopic?: string | null
  prerequisiteChestClicked?: boolean
  lastLevelStatusInfo: LastLevelStatusInfo
  shouldShowCertificateModal: boolean
  currentCertificate: CurriculumCertificate
  isTopicMappingQuizCompleted: boolean
  selectedSubdivision: Record<number, number>
  selectedSubTopic: {
    countryCode: string
    gradeLevel: number
    topicOrder: number
    title: string
    subTopicId: string
    subTopicLevel: number
  } | null
  gradeOverviewFromSanity: GradeOverviewFromSanity | null
  readingTime?: number
}

const initialState: CurriculumSliceType = {
  gradeListByCountry: [],
  gradeContent: {
    gradeLevel: 0,
    gradeLabel: '',
    title: '',
    isJuniorMathGrade: false,
    hasSubdivision: false,
    gradeSubdivisions: [],
  },
  curriculumSubjectContent: curriculumSubjectContentDefault,
  lastActivity: {
    curriculumLevelId: '',
    quarterNo: 0,
    gradeLevel: 0,
  },
  curriculumChangeLoading: false,
  curriculumMappingQuizQuestion: {
    questionId: '',
    quizId: '',
    slug: '',
    title: '',
    description: [],
    isLastQuestion: false,
    isQuizComplete: false,
  },
  shouldResetCurriculumMappingQuiz: false,
  firstMappingQuizTopic: null,
  menuCurriculumDropdownExpanded: false,
  gradePageCurriculumDropdownExpanded: false,
  footerCurriculumDropdownExpanded: false,
  curriculumChangeWarning: false,
  prerequisiteChestClicked: false,
  lastLevelStatusInfo: emptyLastLevelInfo,
  shouldShowCertificateModal: false,
  currentCertificate: {
    id: 0,
    curriculumSubject: '',
    countryCode: '',
    gradeId: '',
    gradeLevel: 0,
    correctness: '',
    date: '',
    topicId: TopicId.ALGEBRA,
    subjectOrder: 0,
    updatedAt: '',
  },
  selectedSubdivision: {},
  selectedSubTopic: null,
  isTopicMappingQuizCompleted: false,
  gradeOverviewFromSanity: null,
}

const slice = createSlice({
  name: 'curriculum',
  initialState,
  reducers: {
    setGradeListByCountry: (state, action: PayloadAction<CurriculumSliceType['gradeListByCountry']>) => ({
      ...state,
      gradeListByCountry: action.payload,
    }),
    setGradeContent(state, action: PayloadAction<CurriculumGradeDataType>) {
      state.gradeContent = action.payload
    },
    setCurriculumSubjectContent(state, action: PayloadAction<CurriculumTopicDataType>) {
      state.curriculumSubjectContent = action.payload
    },
    setLastActivity: (state, action: PayloadAction<CurriculumSliceType['lastActivity']>) => ({
      ...state,
      lastActivity: action.payload,
    }),
    setCurriculumChangeLoading: (state, action: PayloadAction<CurriculumSliceType['curriculumChangeLoading']>) => ({
      ...state,
      curriculumChangeLoading: action.payload,
    }),
    setMenuCurriculumDropdownExpanded: (
      state,
      action: PayloadAction<CurriculumSliceType['menuCurriculumDropdownExpanded']>,
    ) => ({
      ...state,
      menuCurriculumDropdownExpanded: action.payload,
    }),
    setGradePageCurriculumDropdownExpanded: (
      state,
      action: PayloadAction<CurriculumSliceType['gradePageCurriculumDropdownExpanded']>,
    ) => ({
      ...state,
      gradePageCurriculumDropdownExpanded: action.payload,
    }),
    setFooterCurriculumDropdownExpanded: (
      state,
      action: PayloadAction<CurriculumSliceType['footerCurriculumDropdownExpanded']>,
    ) => ({
      ...state,
      footerCurriculumDropdownExpanded: action.payload,
    }),
    setCurriculumChangeWarning: (state, action: PayloadAction<CurriculumSliceType['curriculumChangeWarning']>) => ({
      ...state,
      curriculumChangeWarning: action.payload,
    }),
    closeCurriculumDropdowns: (state) => ({
      ...state,
      menuCurriculumDropdownExpanded: false,
      gradePageCurriculumDropdownExpanded: false,
      footerCurriculumDropdownExpanded: false,
    }),
    setChangeGradeSelection: (state, action: PayloadAction<CurriculumSliceType['changeGradeSelection']>) => ({
      ...state,
      changeGradeSelection: action.payload,
    }),
    setPreviousPage: (state, action: PayloadAction<CurriculumSliceType['previousPage']>) => ({
      ...state,
      previousPage: action.payload,
    }),
    setCurriculumMappingQuizQuestion: (
      state,
      action: PayloadAction<CurriculumSliceType['curriculumMappingQuizQuestion']>,
    ) => ({
      ...state,
      curriculumMappingQuizQuestion: action.payload,
    }),
    setShouldResetCurriculumMappingQuiz: (
      state,
      action: PayloadAction<CurriculumSliceType['shouldResetCurriculumMappingQuiz']>,
    ) => ({
      ...state,
      shouldResetCurriculumMappingQuiz: action.payload,
    }),
    setFirstMappingQuizTopic: (state, action: PayloadAction<CurriculumSliceType['firstMappingQuizTopic']>) => ({
      ...state,
      firstMappingQuizTopic: action.payload,
    }),
    setPrerequisiteChestClicked: (state, action: PayloadAction<CurriculumSliceType['prerequisiteChestClicked']>) => ({
      ...state,
      prerequisiteChestClicked: action.payload,
    }),
    setLastLevelStatusInfo: (state, action: PayloadAction<CurriculumSliceType['lastLevelStatusInfo']>) => ({
      ...state,
      lastLevelStatusInfo: action.payload,
    }),
    setShouldShowCertificateModal: (
      state,
      action: PayloadAction<CurriculumSliceType['shouldShowCertificateModal']>,
    ) => ({
      ...state,
      shouldShowCertificateModal: action.payload,
    }),
    setCurrentCertificate: (state, action: PayloadAction<CurriculumSliceType['currentCertificate']>) => ({
      ...state,
      currentCertificate: action.payload,
    }),

    setSelectedSubdivision: (state, action: PayloadAction<{ gradeLevel: number; index: number }>) => {
      state.selectedSubdivision ??= {}
      state.selectedSubdivision[action.payload.gradeLevel] = action.payload.index
    },
    setSelectedSubTopic: (
      state,
      action: PayloadAction<{
        countryCode: string
        gradeLevel: number
        topicOrder: number
        title: string
        subTopicId: string
        subTopicLevel: number
      } | null>,
    ) => {
      state.selectedSubTopic = action.payload
    },
    setIsTopicMappingQuizCompleted: (
      state,
      action: PayloadAction<CurriculumSliceType['isTopicMappingQuizCompleted']>,
    ) => {
      state.isTopicMappingQuizCompleted = action.payload
    },
    setGradeOverviewFromSanity: (state, action: PayloadAction<GradeOverviewFromSanity>) => {
      state.gradeOverviewFromSanity = action.payload
    },
    setReadingTime: (state, action: PayloadAction<number>) => {
      state.readingTime = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state, action: any) => {
      const newGradeList = action.payload.curriculum.gradeListByCountry
      const newGradeContent = action.payload.curriculum.gradeContent
      const newGradeDetails = action.payload.curriculum.gradeOverviewFromSanity
      const newCurriculumSubjectContent = action.payload.curriculum.curriculumSubjectContent
      return {
        ...state,
        gradeListByCountry: newGradeList?.length > 0 ? newGradeList : state.gradeListByCountry,
        gradeContent: newGradeContent ?? state.gradeContent,
        gradeOverviewFromSanity: newGradeDetails ?? state.gradeOverviewFromSanity,
        curriculumSubjectContent: newCurriculumSubjectContent ?? state.curriculumSubjectContent,
      }
    })
  },
})

export const selectGradeListByCountry = (state: RootState): CurriculumSliceType['gradeListByCountry'] =>
  state.curriculum.gradeListByCountry
export const selectGradeContent = (state: RootState): CurriculumSliceType['gradeContent'] =>
  state.curriculum.gradeContent
export const selectCurriculumSubjectContent = (state: RootState): CurriculumTopicDataType => {
  return state.curriculum.curriculumSubjectContent
}
export const selectGradeTitle = (state: RootState): string => state.curriculum.gradeContent.title
export const selectGradeTitleV2 = (state: RootState): string => state.curriculum.gradeOverviewFromSanity?.title ?? ''
export const selectGradeLevel = (state: RootState): number => state.curriculum.gradeContent.gradeLevel
export const selectSubjectLabel = (state: RootState): string => state.curriculum.curriculumSubjectContent.subjectLabel
export const selectSubjectUrl = (state: RootState): string => state.curriculum.curriculumSubjectContent.subjectUrl ?? ''
export const selectFirstCurriculumVisit = (state: RootState): boolean =>
  state.curriculum.curriculumSubjectContent.isFirstVisit
export const selectIsJuniorMathGrade = (state: RootState): boolean => state.curriculum.gradeContent.isJuniorMathGrade
export const selectLastActivity = (state: RootState): CurriculumSliceType['lastActivity'] =>
  state.curriculum.lastActivity
export const selectViewedGradeLevel = (state: RootState): number => state.curriculum.gradeContent.gradeLevel
export const selectCurriculumChangeLoading = (state: RootState): boolean => state.curriculum.curriculumChangeLoading
export const selectMenuCurriculumDropdownExpanded = (state: RootState): boolean =>
  state.curriculum.menuCurriculumDropdownExpanded
export const selectGradePageCurriculumDropdownExpanded = (state: RootState): boolean =>
  state.curriculum.gradePageCurriculumDropdownExpanded
export const selectFooterCurriculumDropdownExpanded = (state: RootState): boolean =>
  state.curriculum.footerCurriculumDropdownExpanded
export const selectCurriculumChangeWarning = (state: RootState): boolean => state.curriculum.curriculumChangeWarning
export const selectChangeGradeSelection = (state: RootState): CurriculumSliceType['changeGradeSelection'] =>
  state.curriculum.changeGradeSelection
export const selectPreviousPage = (state: RootState): CurriculumSliceType['previousPage'] =>
  state.curriculum.previousPage
export const selectCurriculumMappingQuizQuestion = (
  state: RootState,
): CurriculumSliceType['curriculumMappingQuizQuestion'] => state.curriculum.curriculumMappingQuizQuestion
export const selectShouldResetCurriculumMappingQuiz = (
  state: RootState,
): CurriculumSliceType['shouldResetCurriculumMappingQuiz'] => state.curriculum.shouldResetCurriculumMappingQuiz
export const selectFirstMappingQuizTopic = (state: RootState): CurriculumSliceType['firstMappingQuizTopic'] =>
  state.curriculum.firstMappingQuizTopic ?? null
export const selectPrerequisiteChestClicked = (state: RootState): CurriculumSliceType['prerequisiteChestClicked'] =>
  state.curriculum.prerequisiteChestClicked
export const selectIsLastLevel = (state: RootState): boolean =>
  state.curriculum.lastLevelStatusInfo?.isLastLevel || false
export const selectLastLevelIds = (state: RootState): string[] =>
  state.curriculum.lastLevelStatusInfo?.lastLevelIds || []
export const selectLastLevelGradeInfo = (state: RootState): LastLevelStatusInfo['gradeInfo'] =>
  state.curriculum.lastLevelStatusInfo?.gradeInfo || emptyLastLevelInfo.gradeInfo
export const selectShouldShowCertificateModal = (state: RootState): boolean =>
  state.curriculum.shouldShowCertificateModal
export const selectCurrentCertificate = (state: RootState): CurriculumCertificate => state.curriculum.currentCertificate
export const selectSelectedSubdivision =
  (gradeLevel: number) =>
  (state: RootState): number =>
    state.curriculum.selectedSubdivision?.[gradeLevel] ?? 0
export const selectSelectedSubTopic = (state: RootState): CurriculumSliceType['selectedSubTopic'] =>
  state.curriculum.selectedSubTopic
export const selectIsTopicMappingQuizCompleted = (state: RootState): boolean =>
  state.curriculum.isTopicMappingQuizCompleted
export const selectGradeOverviewFromSanity = (state: RootState): GradeOverviewFromSanity | null =>
  state.curriculum.gradeOverviewFromSanity
export const selectReadingTime = (state: RootState): number | undefined => state.curriculum.readingTime

export const {
  setGradeListByCountry: setGradeListByCountryActionCreator,
  setGradeContent: setGradeContentActionCreator,
  setCurriculumSubjectContent: setCurriculumSubjectContentActionCreator,
  setLastActivity: setLastActivityActionCreator,
  setCurriculumChangeLoading: setCurriculumChangeLoadingActionCreator,
  setMenuCurriculumDropdownExpanded: setMenuCurriculumDropdownExpandedActionCreator,
  setGradePageCurriculumDropdownExpanded: setGradePageCurriculumDropdownExpandedActionCreator,
  setFooterCurriculumDropdownExpanded: setFooterCurriculumDropdownExpandedActionCreator,
  setCurriculumChangeWarning: setCurriculumChangeWarningActionCreator,
  closeCurriculumDropdowns: closeCurriculumDropdownsActionCreator,
  setChangeGradeSelection: setChangeGradeSelectionActionCreator,
  setPreviousPage: setPreviousPageActionCreator,
  setShouldResetCurriculumMappingQuiz: setShouldResetCurriculumMappingQuizActionCreator,
  setCurriculumMappingQuizQuestion: setCurriculumMappingQuizQuestionActionCreator,
  setFirstMappingQuizTopic: setFirstMappingQuizTopicActionCreator,
  setPrerequisiteChestClicked: setPrerequisiteChestClickedActionCreator,
  setLastLevelStatusInfo: setLastLevelStatusInfoActionCreator,
  setShouldShowCertificateModal: setShouldShowCertificateModalActionCreator,
  setCurrentCertificate: setCurrentCertificateActionCreator,
  setSelectedSubdivision: setSelectedSubdivisionActionCreator,
  setSelectedSubTopic: setSelectedSubTopicActionCreator,
  setIsTopicMappingQuizCompleted,
  setGradeOverviewFromSanity: setGradeOverviewFromSanityActionCreator,
  setReadingTime: setReadingTimeActionCreator,
} = slice.actions

export default slice.reducer
